<template>
  <teleport to="body">
    <div
      v-if="showNotification"
      class="fixed inset-0 z-[70] flex items-center justify-center bg-neutral-100/15 backdrop-blur-[4px] p-4"
      @click.self="closeNotification">
      <div
        class="core-modal-surface relative flex max-h-[90vh] w-full max-w-3xl flex-col border border-neutral-300 bg-white">
        <button
          @click="closeNotification"
          class="absolute top-4 right-6 z-10 text-neutral-500 transition-colors hover:text-primary-dark"
          aria-label="Close">
          <IconX class="h-6 w-6" stroke="1.8" />
        </button>

        <div class="overflow-y-auto p-8 pr-12">
          <div class="prose max-w-none" v-html="htmlContent"></div>
        </div>

        <div class="border-t border-neutral-300 p-4 flex justify-end">
          <button
            @click="closeNotification"
            class="core-button-primary core-action-button core-action-button--primary h-11 px-6">
            {{ $t('general.button.ok') }}
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { IconX } from '@tabler/icons-vue'
import { useWidgetText } from '@/helpers/useWidgetText'

const props = defineProps<{
  role?: string
}>()

const SESSION_KEY_PREFIX = 'core_role_popup_seen'
const fallbackSeenRoles = new Set<string>()

const showNotification = ref(false)
const activeRole = computed(() => props.role)
const { wt, wtValue } = useWidgetText('widget-texts-general')

const showPopup = computed(() => {
  if (!activeRole.value) return false
  return wtValue('rolePopups', activeRole.value, 'showPopup') === true
})

const message = computed(() => {
  if (!activeRole.value) return ''
  return wt('rolePopups', activeRole.value, 'messageHtml').trim()
})

const htmlContent = computed(() => message.value)

function sessionKey(role: string): string {
  return `${SESSION_KEY_PREFIX}:${role}`
}

function hasSeenRolePopup(role: string): boolean {
  try {
    return sessionStorage.getItem(sessionKey(role)) === 'true'
  } catch {
    return fallbackSeenRoles.has(role)
  }
}

function markRolePopupSeen(role: string): void {
  try {
    sessionStorage.setItem(sessionKey(role), 'true')
  } catch {
    fallbackSeenRoles.add(role)
  }
}

watch(
  [activeRole, showPopup, message],
  ([role, enabled, text]) => {
    if (!role || !enabled || !text || hasSeenRolePopup(role)) return

    markRolePopupSeen(role)
    showNotification.value = true
  },
  { immediate: true }
)

function closeNotification(): void {
  showNotification.value = false
}
</script>

<style scoped>
.core-modal-surface {
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.12),
    0 8px 16px -12px rgba(0, 0, 0, 0.2);
}

.prose {
  color: #333333;
}

.prose :deep(h1) {
  font-size: 1.875rem;
  font-weight: bold;
  margin-bottom: 1.25rem;
  margin-top: 1.5rem;
}

.prose :deep(h2) {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 1rem;
  margin-top: 1.25rem;
}

.prose :deep(h3) {
  font-size: 1.25rem;
  font-weight: bold;
  margin-bottom: 0.75rem;
  margin-top: 1rem;
}

.prose :deep(p) {
  margin-bottom: 1rem;
  line-height: 1.6;
}

.prose :deep(img) {
  display: block;
  height: auto;
  max-width: 100%;
  margin: 1.25rem auto;
}

.prose :deep(picture) {
  display: block;
  margin: 1.25rem auto;
  max-width: 100%;
}

.prose :deep(picture img) {
  margin: 0 auto;
}

.prose :deep(ul) {
  margin-left: 1.5rem;
  margin-bottom: 1rem;
  list-style: disc;
}

.prose :deep(ol) {
  margin-left: 1.5rem;
  margin-bottom: 1rem;
  list-style: decimal;
}

.prose :deep(li) {
  margin-bottom: 0.5rem;
}

.prose :deep(strong) {
  font-weight: 600;
}

.prose :deep(em) {
  font-style: italic;
}

.prose :deep(hr) {
  border: 0;
  border-top: 2px solid #e5e5e5;
  margin: 1.5rem 0;
}

.prose :deep(code) {
  background-color: #f5f5f5;
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-size: 0.875em;
  font-family: monospace;
}

.prose :deep(pre) {
  background-color: #f5f5f5;
  padding: 1rem;
  border-radius: 0.5rem;
  overflow-x: auto;
  margin-bottom: 1rem;
}

.prose :deep(blockquote) {
  border-left: 4px solid #e5e5e5;
  padding-left: 1rem;
  margin: 1rem 0;
  color: #6e6e6e;
}
</style>
